/**
 * DashboardPage Component
 * Premium dashboard with stats, job types, and recent jobs
 */

import React, { useState, useEffect } from "react";
import { AnalyticsOverview } from "../components/dashboard";
import { JobTypeGrid } from "../components/jobs/JobTypeGrid";
import { RecentJobs } from "../components/jobs/RecentJobs";
import { CreateJobModal } from "../components/jobs/CreateJobModal";
import { useAppDispatch, jobsActions, useAppSelector } from "../redux";
import { JobType } from "../types";
import { BarChart3, Plus, TrendingUp, Sparkles } from "lucide-react";
import { Button } from "../components/common";

export const DashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedJobType, setSelectedJobType] = useState<JobType | null>(null);

  // Fetch jobs on mount
  useEffect(() => {
    dispatch(jobsActions.fetchJobs({ page: 1, page_size: 10 }));
    dispatch(jobsActions.fetchJobStats());
  }, [dispatch]);

  const handleSelectJobType = (jobType: JobType) => {
    setSelectedJobType(jobType);
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setSelectedJobType(null);
  };

  const handleJobCreated = () => {
    dispatch(jobsActions.fetchJobs({ page: 1, page_size: 10 }));
    dispatch(jobsActions.fetchJobStats());
  };

  const handleRefresh = () => {
    dispatch(jobsActions.fetchJobs({ page: 1, page_size: 10 }));
    dispatch(jobsActions.fetchJobStats());
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="min-h-screen bg-navy-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                {getGreeting()}, {user?.first_name || "there"}
              </span>
              <Sparkles className="w-8 h-8 text-primary-400" />
            </h1>
            <p className="text-slate-400 text-lg">
              Launch new analyses, view your stats, and explore powerful insights.
            </p>
          </div>
          <Button
            variant="gradient"
            size="lg"
            onClick={() => setIsCreateModalOpen(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            New Analysis
          </Button>
        </div>

        {/* Analytics Overview */}
        <section className="mb-10">
          <AnalyticsOverview />
        </section>

        {/* Job Type Selection */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20">
              <BarChart3 className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                Select Analysis Type
              </h2>
              <p className="text-sm text-slate-400">
                Choose the type of analysis to run on your media
              </p>
            </div>
          </div>
          <JobTypeGrid onSelectJobType={handleSelectJobType} />
        </section>

        {/* Recent Jobs */}
        <section>
          <RecentJobs onRefresh={handleRefresh} />
        </section>
      </div>

      {/* Create Job Modal */}
      <CreateJobModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleJobCreated}
        selectedJobType={selectedJobType}
      />
    </div>
  );
};
